/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Industries — Apollo Bearing Index (bearing interchange search)
 * assets/interchange-search.js
 *
 * Public, ungated client for the interchange tool embedded on
 * /pages/bearing-interchange. Vanilla ES2019+, IIFE, no globals (consumes
 * window.IFX from interchange-utils.js, loaded first by the snippet).
 *
 * Feature map:
 *   §1  IFX bridge (degraded fallbacks if utils failed to load)
 *   §2  Root guard, config, element refs
 *   §3  Unit conversion (mm/inch + fractional-inch parsing — ported verbatim)
 *   §4  Shared render helpers: status, skeletons, error states
 *   §5  URL state (?tab=…&q=… deep links, popstate, copy link)
 *   §6  Tabs (roving tabindex + arrow keys)
 *   §7  Search: /api/search (PN + dimensional), stale-response guard
 *   §8  Results rendering: cards, dims grouping, banners, reverse lookup
 *   §9  Filter bar + sort (client-side facets over the in-memory hit list)
 *   §10 (removed — commerce CTAs disabled; RFQ is the only card action)
 *   §11 (removed — bulk CSV export and print disabled by data policy)
 *   §12 PN autocomplete (combobox ARIA, loading/empty rows, stale guard)
 *   §13 Decode + glossary (/api/decode)
 *   §14 Equipment: free-text machine search, cascade browse, machineId
 *   §15 Catalogs (/api/catalogs, lazy cache, brand chips)
 *   §16 RFQ dialog (native <dialog>.showModal + focus-trap fallback)
 *   §17 Misc a11y: unit radiogroup arrows, disclaimer Esc, doc-level clicks
 *   §18 Init: wire toolbar actions, parse URL, popstate
 *
 * API contracts consumed (base URL from data-api-base):
 *   GET  /api/search?q=&limit=50 | ?bore=&od=&width=[&tol=]
 *   GET  /api/autocomplete?q=
 *   GET  /api/decode?q= | (none → glossary)
 *   GET  /api/equipment [?manufacturer=][&model=] | ?q= | ?machineId= | ?bearing=
 *   GET  /api/catalogs
 *   POST /api/rfq
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  /* ── §1 IFX bridge ─────────────────────────────────────────────────────
     interchange-utils.js installs window.IFX before this file runs. If it
     is missing (asset failed to upload / blocked), log loudly and fall back
     to safe local implementations so the tool still works. */

  var IFX = window.IFX;
  if (!IFX) {
    try { console.error("[interchange] interchange-utils.js missing — using degraded local fallbacks"); } catch (e) { /* noop */ }
    IFX = {
      escapeHtml: function (v) {
        return String(v == null ? "" : v)
          .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
      },
      escapeAttr: function (v) {
        return String(v == null ? "" : v).replace(/[&<>"']/g, function (c) {
          return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
        });
      },
      debounce: function (fn, ms) {
        var t = null;
        return function () {
          var self = this, args = arguments;
          if (t) clearTimeout(t);
          t = setTimeout(function () { fn.apply(self, args); }, ms);
        };
      },
      formatMoney: function (cents) {
        var n = Number(cents);
        return Number.isFinite(n) ? "$" + (n / 100).toFixed(2) : "";
      },
      showToast: function () { /* degraded: no visual toast */ },
      track: function () { /* degraded: no analytics */ },
    };
  }

  var esc = IFX.escapeHtml;
  var escAttr = IFX.escapeAttr;

  /* ── §2 Root guard, config, element refs ───────────────────────────── */

  const root = document.querySelector(".interchange-search");
  if (!root) return; // only guard: missing root. The tool is public/ungated.

  const API_BASE = root.dataset.apiBase || "";
  const MM_PER_INCH = 25.4;
  const AC_DEBOUNCE_MS = 200;   // PN autocomplete
  const OEM_DEBOUNCE_MS = 250;  // equipment free-text search
  const DEFAULT_TOL = 0.1;      // "Exact" tolerance — not sent to the API

  const tabs = root.querySelectorAll(".interchange-search__tab");
  const forms = root.querySelectorAll(".interchange-search__form");
  const status = root.querySelector(".interchange-search__status");
  const results = root.querySelector(".interchange-search__results");
  const toolbar = root.querySelector(".interchange-search__toolbar");
  const filtersEl = root.querySelector(".interchange-search__filters");
  const sortEl = root.querySelector(".interchange-search__sort");
  const unitButtons = root.querySelectorAll(".interchange-search__unit");
  const pnInput = root.querySelector('input[name="q"]');
  const acDropdown = root.querySelector(".interchange-search__autocomplete");
  const dimsForm = root.querySelector('.interchange-search__form[data-mode="dims"]');
  const tolSelect = root.querySelector(".interchange-search__tol");
  const oemForm = root.querySelector('.interchange-search__form[data-mode="oem"]');
  const oemSearchInput = oemForm ? oemForm.querySelector(".interchange-search__oem-search") : null;
  const oemSuggest = oemForm ? oemForm.querySelector(".interchange-search__oem-suggest") : null;
  const mfgSelect = oemForm ? oemForm.querySelector('select[name="manufacturer"]') : null;
  const modelSelect = oemForm ? oemForm.querySelector('select[name="model"]') : null;
  const catalogsPanel = root.querySelector('.interchange-search__form[data-mode="catalogs"]');
  const catalogFilters = catalogsPanel ? catalogsPanel.querySelector(".interchange-search__catalog-filters") : null;
  const disclaimer = root.querySelector(".interchange-search__disclaimer");
  const modal = root.querySelector('.interchange-search__modal[data-modal="rfq"]');
  const rfqForm = modal ? modal.querySelector(".interchange-search__rfq-form") : null;
  const modalError = modal ? modal.querySelector(".interchange-search__modal-error") : null;

  // ── State ──────────────────────────────────────────────────────────────
  let currentUnit = "mm";
  let currentTab = "pn";       // default tab is pn/Interchange (matches markup)
  let searchSeq = 0;           // stale-response guard: /api/search + equipment results
  let acSeq = 0;               // stale-response guard: autocomplete
  let oemAcSeq = 0;            // stale-response guard: equipment free-text search
  let lastSearch = null;       // { mode, params, data, hits, queryEcho, label }
  let activeFacets = {};       // { facetKey: Set<label> }
  let sortKey = "relevance";
  let didPushState = false;    // first executed search pushes, later ones replace
  let restoringUrl = false;    // suppress history writes while restoring from URL

  function submitForm(form) {
    if (!form) return;
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  }

  /* ── §3 Unit conversion (ported verbatim from the previous build) ────── */

  function mmToInches(mm) {
    return Math.round((mm / MM_PER_INCH) * 1000) / 1000;
  }
  function inchesToMm(inches) {
    return Math.round(inches * MM_PER_INCH * 1000) / 1000;
  }

  /**
   * Parse a fractional-inch string ("1-1/4", "5/8", "1.000", '1"', "1 1/4").
   * Returns a number in inches, or null if unparseable.
   */
  function parseFractionInches(input) {
    if (input == null) return null;
    let s = String(input).trim();
    if (!s) return null;
    s = s.replace(/["”]$/, "").replace(/\s*in\.?$/i, "").trim();
    if (!s) return null;
    if (/^-?\d*\.\d+$/.test(s) || /^-?\d+$/.test(s)) {
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    let m = /^(\d+)\s*\/\s*(\d+)$/.exec(s);
    if (m) {
      const num = Number(m[1]), den = Number(m[2]);
      return den === 0 ? null : num / den;
    }
    m = /^(\d+)[\s\-]+(\d+)\s*\/\s*(\d+)$/.exec(s);
    if (m) {
      const whole = Number(m[1]), num = Number(m[2]), den = Number(m[3]);
      return den === 0 ? null : whole + num / den;
    }
    return null;
  }

  /**
   * Parse a dimension input string. Returns mm number or null.
   * If `unit` is "in", interprets fractions; if "mm", interprets decimal.
   */
  function parseDimension(input, unit) {
    if (input == null) return null;
    const s = String(input).trim();
    if (!s) return null;
    if (unit === "mm") {
      const n = Number(s.replace(/\s*mm\s*$/i, ""));
      return Number.isFinite(n) ? n : null;
    }
    const inches = parseFractionInches(s);
    return inches === null ? null : inchesToMm(inches);
  }

  function formatDims(boreMm, odMm, widthMm, unit) {
    const parts = [];
    if (boreMm != null && odMm != null && widthMm != null) {
      if (unit === "in") {
        const i = (mm) => mmToInches(mm).toFixed(3);
        parts.push(`${i(boreMm)}" × ${i(odMm)}" × ${i(widthMm)}"`);
        parts.push(`${boreMm} × ${odMm} × ${widthMm} mm`);
      } else {
        parts.push(`${boreMm} × ${odMm} × ${widthMm} mm`);
        const i = (mm) => mmToInches(mm).toFixed(3);
        parts.push(`${i(boreMm)}" × ${i(odMm)}" × ${i(widthMm)}"`);
      }
    }
    return parts;
  }

  /* ── §4 Shared render helpers ────────────────────────────────────────── */

  function setStatus(html, isError = false) {
    if (!status) return;
    status.innerHTML = html || "";
    status.classList.toggle("interchange-search__status--error", !!isError);
  }

  function spinnerHtml() {
    return '<span class="interchange-search__spinner" aria-hidden="true"></span>';
  }

  function hideToolbar() {
    if (toolbar) toolbar.hidden = true;
  }

  function clearResults(alsoStatus = true) {
    if (results) results.innerHTML = "";
    hideToolbar();
    if (alsoStatus) setStatus("");
  }

  /**
   * Render loading skeletons into the results grid.
   *   kind "cards" → 6 shimmering result-card placeholders (search, equipment,
   *                  catalogs)
   *   kind "block" → one wide shimmer block (decode, glossary)
   */
  function renderSkeletons(kind) {
    if (!results) return;
    hideToolbar();
    results.innerHTML = "";
    if (kind === "block") {
      const block = document.createElement("div");
      block.className = "interchange-search__skeleton-block";
      block.setAttribute("aria-hidden", "true");
      results.appendChild(block);
      return;
    }
    for (let i = 0; i < 6; i++) {
      const card = document.createElement("div");
      card.className = "interchange-search__card interchange-search__card--skeleton";
      card.setAttribute("aria-hidden", "true");
      card.innerHTML =
        '<div class="interchange-search__skeleton-bar interchange-search__skeleton-bar--badge"></div>' +
        '<div class="interchange-search__skeleton-bar interchange-search__skeleton-bar--brand"></div>' +
        '<div class="interchange-search__skeleton-bar interchange-search__skeleton-bar--pn"></div>' +
        '<div class="interchange-search__skeleton-bar interchange-search__skeleton-bar--spec"></div>' +
        '<div class="interchange-search__skeleton-bar interchange-search__skeleton-bar--type"></div>' +
        '<div class="interchange-search__skeleton-bar interchange-search__skeleton-bar--cta"></div>';
      results.appendChild(card);
    }
  }

  /**
   * Standard fetch-failure state. `retryFn` re-runs the exact same request.
   */
  function renderError(title, detail, retryFn) {
    if (!results) return;
    hideToolbar();
    results.innerHTML = "";
    const box = document.createElement("div");
    box.className = "interchange-search__error";
    box.innerHTML =
      '<div class="interchange-search__error-title">' + esc(title) + "</div>" +
      "<p>" + esc(detail) + "</p>";
    if (typeof retryFn === "function") {
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "interchange-search__retry";
      retry.textContent = "Try again";
      retry.addEventListener("click", retryFn);
      box.appendChild(retry);
    }
    results.appendChild(box);
    setStatus(esc(title), true);
  }

  /* ── §5 URL state ─────────────────────────────────────────────────────
     Owned params: tab, q, bore, od, width, tol, unit, machineId. Everything
     else in the query string (theme params, utm, etc.) is preserved. Dims
     values are ALWAYS stored in mm; `unit=in` is display-only. */

  const OWNED_PARAMS = ["tab", "q", "bore", "od", "width", "tol", "unit", "machineId"];

  function writeUrlState(state) {
    if (restoringUrl) return; // URL already correct while restoring
    try {
      const sp = new URLSearchParams(location.search);
      OWNED_PARAMS.forEach((p) => sp.delete(p));
      Object.keys(state).forEach((k) => {
        const v = state[k];
        if (v != null && v !== "") sp.set(k, String(v));
      });
      const qs = sp.toString();
      const newUrl = location.pathname + (qs ? "?" + qs : "") + location.hash;
      const histState = { ifx: state };
      if (!didPushState) {
        history.pushState(histState, "", newUrl);
        didPushState = true;
      } else {
        history.replaceState(histState, "", newUrl);
      }
    } catch (err) { /* history API unavailable — non-fatal */ }
  }

  /* ── §6 Tabs ──────────────────────────────────────────────────────────
     Roving tabindex + Arrow/Home/End keys (WAI-ARIA tabs pattern). */

  let oemLoaded = false;
  const tabList = Array.from(tabs);

  function syncTabState() {
    tabList.forEach((b) => {
      const active = b.classList.contains("is-active");
      b.setAttribute("aria-selected", active ? "true" : "false");
      b.tabIndex = active ? 0 : -1;
    });
  }

  function activateTab(btn, opts) {
    opts = opts || {};
    const target = btn.dataset.tab;
    const changed = currentTab !== target;
    currentTab = target;
    tabList.forEach((b) => b.classList.toggle("is-active", b === btn));
    forms.forEach((f) => f.classList.toggle("is-active", f.dataset.mode === target));
    syncTabState();
    clearResults();
    if (target === "oem" && !oemLoaded) {
      oemLoaded = true;
      loadManufacturers();
    }
    if (target === "catalogs") openCatalogs();
    if (changed && !opts.silent) IFX.track("ifx_tab_change", { tab: target });
  }

  function activateTabByName(name, opts) {
    const btn = tabList.find((b) => b.dataset.tab === name);
    if (btn) activateTab(btn, opts);
  }

  tabList.forEach((btn) => {
    btn.addEventListener("click", () => activateTab(btn));
    btn.addEventListener("keydown", (e) => {
      let idx = tabList.indexOf(btn);
      if (e.key === "ArrowRight" || e.key === "ArrowDown") idx = (idx + 1) % tabList.length;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") idx = (idx - 1 + tabList.length) % tabList.length;
      else if (e.key === "Home") idx = 0;
      else if (e.key === "End") idx = tabList.length - 1;
      else return;
      e.preventDefault();
      tabList[idx].focus();
      activateTab(tabList[idx]);
    });
  });
  syncTabState();

  /* ── §7 Search (/api/search) ─────────────────────────────────────────── */

  function dimsLabel(params) {
    const f = (v) => (v == null ? "–" : String(v));
    return f(params.bore) + " × " + f(params.od) + " × " + f(params.width) + " mm";
  }

  function executePnSearch(q) {
    runSearch("pn", { q: q });
  }

  function executeDimsSearch(params) {
    runSearch("dims", params);
  }

  async function runSearch(mode, params) {
    const seq = ++searchSeq;
    const label = mode === "pn" ? params.q : dimsLabel(params);

    // URL state — synchronous, before the first await (restore-guarded).
    if (mode === "pn") {
      writeUrlState({ tab: "pn", q: params.q });
    } else {
      writeUrlState({
        tab: "dims",
        bore: params.bore != null ? params.bore : null,
        od: params.od != null ? params.od : null,
        width: params.width != null ? params.width : null,
        tol: params.tol && Number(params.tol) !== DEFAULT_TOL ? params.tol : null,
        unit: currentUnit === "in" ? "in" : null,
      });
    }

    setStatus(spinnerHtml() + "Searching…");
    renderSkeletons("cards");

    const url = new URL(API_BASE + "/api/search");
    if (mode === "pn") {
      url.searchParams.set("q", params.q);
      url.searchParams.set("limit", "50");
    } else {
      if (params.bore != null) url.searchParams.set("bore", String(params.bore));
      if (params.od != null) url.searchParams.set("od", String(params.od));
      if (params.width != null) url.searchParams.set("width", String(params.width));
      if (params.tol && Number(params.tol) !== DEFAULT_TOL) url.searchParams.set("tol", String(params.tol));
    }

    try {
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      if (seq !== searchSeq) return; // stale — a newer search superseded us

      const hits = (data.primary ? [data.primary] : []).concat(data.equivalents || []);
      lastSearch = {
        mode: mode,
        params: params,
        data: data,
        hits: hits,
        queryEcho: data.queryEcho || {},
        label: label,
      };
      resetFilters(mode);
      renderSearchResults();

      IFX.track("ifx_search", Object.assign({
        mode: mode,
        query: label,
        result_count: hits.length,
        had_apollo_match: hits.some((h) => !!h.apolloMatch),
      }, mode === "dims" && params.tol ? { tol: Number(params.tol) } : {}));
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error("[interchange] search error", err);
      renderError(
        "Search unavailable",
        "We couldn't reach the search service. Check your connection and try again.",
        () => runSearch(mode, params)
      );
    }
  }

  // Submit wiring for the pn / dims / decode panels.
  forms.forEach((form) => {
    if (form.tagName !== "FORM") return; // catalogs panel is a <div>
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      hideAutocomplete();
      const mode = form.dataset.mode;
      if (mode === "pn") {
        const q = pnInput ? pnInput.value.trim() : "";
        if (!q) return setStatus("Enter a part number.", true);
        executePnSearch(q);
      } else if (mode === "dims") {
        const fd = new FormData(form);
        const boreMm = parseDimension(fd.get("bore"), currentUnit);
        const odMm = parseDimension(fd.get("od"), currentUnit);
        const widthMm = parseDimension(fd.get("width"), currentUnit);
        const allBlank = !fd.get("bore") && !fd.get("od") && !fd.get("width");
        if (allBlank) return setStatus("Enter at least one dimension.", true);
        const params = {};
        if (boreMm != null) params.bore = boreMm;
        if (odMm != null) params.od = odMm;
        if (widthMm != null) params.width = widthMm;
        params.tol = tolSelect ? Number(tolSelect.value) || DEFAULT_TOL : DEFAULT_TOL;
        executeDimsSearch(params);
      } else if (mode === "decode") {
        const fd = new FormData(form);
        const q = (fd.get("decode_q") || "").toString().trim();
        if (!q) return setStatus("Enter a part number to decode.", true);
        runDecode(q);
      }
    });
  });

  /* ── §8 Results rendering ────────────────────────────────────────────── */

  // Provenance badges (per-hit xrefType). "family" / missing renders nothing.
  const XREF_BADGES = {
    verified: { cls: "verified", label: "Verified interchange" },
    evidence: { cls: "evidence", label: "Catalog cross-ref" },
    evidence_unverified: { cls: "unverified", label: "Cross-ref (unverified dims)" },
  };

  // Match-tier labels (facet + CSV). No xrefType → family match.
  function tierLabel(h) {
    if (!h.xrefType) return "Family match";
    if (h.xrefType === "verified") return "Verified";
    if (h.xrefType === "evidence") return "Catalog cross-ref";
    if (h.xrefType === "evidence_unverified") return "Unverified";
    return String(h.xrefType);
  }

  // Human labels for canonical attribute values; unknown values fall back to
  // the raw code. Hits with no value bucket as "Standard".
  const ATTR_LABELS = {
    SEAL_C_2: "Contact seals, both sides",
    SEAL_C_1: "Contact seal, one side",
    SEAL_NC_2: "Non-contact seals, both sides",
    SEAL_NC_1: "Non-contact seal, one side",
    SHIELD_2: "Shields, both sides",
    SHIELD_1: "Shield, one side",
    OPEN: "Open (no seals)",
    C2: "C2 clearance",
    C3: "C3 clearance",
    C4: "C4 clearance",
    C5: "C5 clearance",
    CN: "Normal clearance",
    CAGE_BRASS: "Brass cage",
    CAGE_POLY: "Polyamide cage",
    CAGE_STEEL: "Steel cage",
    P4: "P4 precision",
    P5: "P5 precision",
    P6: "P6 precision",
  };

  function attrLabel(hit, name) {
    const v = hit.attributes && hit.attributes[name];
    if (!v) return "Standard";
    return ATTR_LABELS[v] || String(v);
  }

  function xrefBadgeHtml(b) {
    const meta = Object.prototype.hasOwnProperty.call(XREF_BADGES, b.xrefType)
      ? XREF_BADGES[b.xrefType]
      : null;
    if (!meta) return "";
    return '<span class="interchange-search__xref-badge interchange-search__xref-badge--' + meta.cls + '">'
      + esc(meta.label) + "</span>";
  }

  function confChipHtml(b) {
    const bits = [];
    if (typeof b.interchangeType === "string" && b.interchangeType) {
      bits.push(b.interchangeType.replace(/_/g, " "));
    }
    if (typeof b.confidence === "number" && b.confidence > 0 && b.confidence <= 1) {
      bits.push(Math.round(b.confidence * 100) + "%");
    }
    if (!bits.length) return "";
    return '<span class="interchange-search__conf-chip">' + esc(bits.join(" · ")) + "</span>";
  }

  function specValue(mm) {
    if (mm == null) return "–";
    return currentUnit === "in" ? mmToInches(mm).toFixed(3) + '"' : mm + " mm";
  }

  function specRowHtml(b) {
    if (b.boreMm == null && b.odMm == null && b.widthMm == null) return "";
    const cell = (label, mm) =>
      '<span class="interchange-search__spec">'
      + '<span class="interchange-search__spec-label">' + label + "</span>"
      + '<span class="interchange-search__spec-value">' + esc(specValue(mm)) + "</span>"
      + "</span>";
    return '<div class="interchange-search__card-specs">'
      + cell("ID", b.boreMm) + cell("OD", b.odMm) + cell("W", b.widthMm)
      + "</div>";
  }

  function altUnitLineHtml(b) {
    const dims = formatDims(b.boreMm, b.odMm, b.widthMm, currentUnit);
    return dims.length === 2
      ? '<div class="interchange-search__card-dims-secondary">' + esc(dims[1]) + "</div>"
      : "";
  }

  // Per-card deviation line vs the dims query (only when tol > 0.1).
  function deviationLineHtml(b) {
    if (!lastSearch || lastSearch.mode !== "dims") return "";
    const tol = Number(lastSearch.params.tol) || DEFAULT_TOL;
    if (tol <= DEFAULT_TOL) return "";
    const echo = lastSearch.queryEcho || {};
    const parts = [];
    const push = (name, echoVal, hitVal) => {
      const e = Number(echoVal);
      if (!Number.isFinite(e) || hitVal == null) return;
      const delta = Math.round((Number(hitVal) - e) * 100) / 100;
      if (Math.abs(delta) < 0.05) return;
      parts.push(name + " " + (delta > 0 ? "+" : "−") + Math.abs(delta) + " mm");
    };
    push("bore", echo.bore, b.boreMm);
    push("od", echo.od, b.odMm);
    push("width", echo.width, b.widthMm);
    if (!parts.length) return "";
    return '<div class="interchange-search__card-dims-secondary">Deviation: ' + esc(parts.join(" · ")) + "</div>";
  }

  function addRfqButton(cta, partNumber, brand) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "interchange-search__btn-rfq";
    btn.textContent = "Request a quote";
    btn.addEventListener("click", () => {
      IFX.track("ifx_cta_click", { kind: "rfq" });
      openRfq(partNumber, brand);
    });
    cta.appendChild(btn);
  }

  function addFindInterchangesButton(cta, partNumber) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "interchange-search__btn-secondary";
    btn.textContent = "Find interchanges";
    btn.addEventListener("click", () => {
      IFX.track("ifx_cta_click", { kind: "xref_jump" });
      activateTabByName("pn");
      if (pnInput) {
        pnInput.value = partNumber || "";
        submitForm(pnInput.form);
      }
    });
    cta.appendChild(btn);
  }

  function buildCard(b, isPrimary) {
    const card = document.createElement("div");
    card.className = "interchange-search__card"
      + (isPrimary ? " interchange-search__card--primary" : "");
    const brand = (b.brand || "").toUpperCase();
    const badges = xrefBadgeHtml(b) + confChipHtml(b);
    card.innerHTML =
      (badges ? '<div class="interchange-search__card-badges">' + badges + "</div>" : "")
      + '<div class="interchange-search__card-brand">' + esc(brand) + "</div>"
      + '<div class="interchange-search__card-pn">' + esc(b.partNumber || "") + "</div>"
      + specRowHtml(b)
      + altUnitLineHtml(b)
      + deviationLineHtml(b)
      + (b.bearingType ? '<div class="interchange-search__card-type">' + esc(b.bearingType.replace(/_/g, " ")) + "</div>" : "")
      + '<div class="interchange-search__card-cta"></div>';
    const cta = card.querySelector(".interchange-search__card-cta");
    addRfqButton(cta, b.partNumber, b.brand);
    if (lastSearch && lastSearch.mode === "dims") {
      addFindInterchangesButton(cta, b.partNumber);
    }
    return card;
  }

  function buildOemMatchBanner(om) {
    const banner = document.createElement("div");
    banner.className = "interchange-search__oem-match-banner";
    const machinesList = (om.machines || []).slice(0, 6).map((m) =>
      "<li><strong>" + esc(m.manufacturer) + " " + esc(m.model) + "</strong>"
      + (m.location ? " &mdash; " + esc(m.location) : "") + "</li>"
    ).join("");
    const more = (om.machines || []).length > 6
      ? '<li class="interchange-search__oem-match-more">+ ' + (om.machines.length - 6)
        + " more machine" + (om.machines.length - 6 === 1 ? "" : "s") + "</li>"
      : "";
    banner.innerHTML =
      '<div class="interchange-search__oem-match-header">OEM part match</div>'
      + '<div class="interchange-search__oem-match-body">'
      + "That OEM part number is bearing <strong>"
      + esc((om.bearingBrand || "").toUpperCase()) + " " + esc(om.bearingPn || "")
      + "</strong>" + (om.bearingType ? " &mdash; " + esc(om.bearingType.replace(/_/g, " ")) : "")
      + ". Below are the cross-brand equivalents.</div>"
      + '<div class="interchange-search__oem-match-machines">Found in:<ul>'
      + machinesList + more + "</ul></div>";
    return banner;
  }

  // Suffix-relaxed fallback notice: the API found no row matching the exact
  // requested variant (e.g. "6205 C3") and re-ran with family/base matching
  // only (response.fallback === "suffix_relaxed"). Explain what was relaxed,
  // using the shared ATTR_LABELS map; unknown canonical values fall back to
  // the raw code with underscores humanized.
  function relaxedAttrLabel(v) {
    return ATTR_LABELS[v] || String(v).replace(/_/g, " ");
  }

  function buildSuffixRelaxedBanner(data) {
    const attrs = data.relaxedAttributes || {};
    const list = Object.keys(attrs).map((k) => relaxedAttrLabel(attrs[k])).join(", ");
    const base = data.relaxedBase || (data.queryEcho && data.queryEcho.q) || "";
    const banner = document.createElement("div");
    banner.className = "interchange-search__banner interchange-search__banner--relaxed";
    banner.innerHTML =
      '<div class="interchange-search__banner-title">No exact variant record</div>'
      + '<div class="interchange-search__banner-body">No exact record for the requested variant'
      + (list ? " (<strong>" + esc(list) + "</strong>)" : "")
      + " &mdash; showing <strong>" + esc(base) + "</strong> family cross-references. "
      + "Suffix variants usually share the same interchange; confirm details when ordering.</div>";
    return banner;
  }

  /**
   * Master render for pn/dims search results. Re-run on every filter/sort
   * change (no refetch — operates on the in-memory hit list).
   */
  function renderSearchResults() {
    if (!lastSearch || !results) return;
    const mode = lastSearch.mode;
    const data = lastSearch.data;
    const hits = lastSearch.hits;
    results.innerHTML = "";

    if (hits.length === 0) {
      hideToolbar();
      // An OEM reverse-lookup banner is context, not a result — still show it.
      if (mode === "pn" && data.oemMatch) results.appendChild(buildOemMatchBanner(data.oemMatch));
      const empty = document.createElement("div");
      empty.className = "interchange-search__empty";
      empty.innerHTML =
        '<div class="interchange-search__empty-title">No matches found in our database</div>'
        + "<p>Submit a quote request and our team will look it up directly.</p>";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "interchange-search__btn-rfq";
      btn.textContent = "Request a quote";
      btn.addEventListener("click", () => {
        IFX.track("ifx_cta_click", { kind: "rfq" });
        openRfq(mode === "pn" ? lastSearch.params.q : "", "");
      });
      empty.appendChild(btn);
      results.appendChild(empty);
      setStatus("No results for " + esc(lastSearch.label) + ".");
      return;
    }

    renderToolbar();

    // Contextual banners (pn mode).
    if (mode === "pn" && data.oemMatch) results.appendChild(buildOemMatchBanner(data.oemMatch));
    if (mode === "pn" && data.fallback === "suffix_relaxed") {
      results.appendChild(buildSuffixRelaxedBanner(data));
    }

    const primary = data.primary || null;
    const equivalents = primary ? hits.slice(1) : hits.slice();
    const filtered = applyFiltersAndSort(equivalents);
    let shown = filtered.length;

    if (primary) {
      // Primary card is exempt from filtering — always rendered.
      results.appendChild(buildCard(primary, true));
      shown += 1;
    }

    if (mode === "dims") {
      renderDimsGroups(filtered);
    } else {
      filtered.forEach((h) => results.appendChild(buildCard(h, false)));
    }

    const n = hits.length;
    let msg = n + " " + (n === 1 ? "result" : "results") + " for " + esc(lastSearch.label) + ".";
    if (filtersActive()) msg += " Showing " + shown + " of " + n + ".";
    setStatus(msg);
  }

  // Dims mode: group by exact (bore, od, width); headers ordered by
  // dimDeltaMm; card order within a group follows the active sort.
  function renderDimsGroups(list) {
    const groups = new Map();
    list.forEach((h, i) => {
      const key = [h.boreMm, h.odMm, h.widthMm].join("|");
      let g = groups.get(key);
      if (!g) {
        g = { boreMm: h.boreMm, odMm: h.odMm, widthMm: h.widthMm, items: [], minDelta: Infinity, firstIdx: i };
        groups.set(key, g);
      }
      g.items.push(h);
      if (typeof h.dimDeltaMm === "number" && h.dimDeltaMm < g.minDelta) g.minDelta = h.dimDeltaMm;
    });
    const ordered = Array.from(groups.values()).sort((a, b) =>
      (a.minDelta - b.minDelta) || (a.firstIdx - b.firstIdx)
    );
    const fmt = (v) => (v == null ? "–" : String(v));
    ordered.forEach((g) => {
      const brands = new Set(g.items.map((h) => (h.brand || "").toLowerCase()));
      const bn = brands.size;
      const header = document.createElement("div");
      header.className = "interchange-search__size-group";
      header.innerHTML =
        '<span class="interchange-search__size-group-dims">'
        + esc(fmt(g.boreMm) + " × " + fmt(g.odMm) + " × " + fmt(g.widthMm) + " mm") + "</span>"
        + '<span class="interchange-search__size-group-count">' + bn + " brand" + (bn === 1 ? "" : "s") + "</span>";
      results.appendChild(header);
      g.items.forEach((h) => results.appendChild(buildCard(h, false)));
    });
  }

  /* ── §9 Filter bar + sort ────────────────────────────────────────────── */

  const FACETS = [
    { key: "brand", label: "Brand", get: (h) => (h.brand || "").toUpperCase() || "Other" },
    { key: "seal", label: "Seal / shield", get: (h) => attrLabel(h, "seal") },
    { key: "clearance", label: "Clearance", get: (h) => attrLabel(h, "clearance") },
    { key: "cage", label: "Cage", get: (h) => attrLabel(h, "cage") },
    { key: "type", label: "Bearing type", get: (h) => (h.bearingType ? String(h.bearingType).replace(/_/g, " ") : "Other") },
    { key: "tier", label: "Match tier", get: tierLabel },
  ];

  function resetFilters(mode) {
    activeFacets = {};
    sortKey = "relevance";
  }

  function filtersActive() {
    return Object.keys(activeFacets).some((k) => activeFacets[k] && activeFacets[k].size > 0);
  }

  function matchesFilters(h) {
    for (let i = 0; i < FACETS.length; i++) {
      const f = FACETS[i];
      const set = activeFacets[f.key];
      if (set && set.size > 0 && !set.has(f.get(h))) return false;
    }
    return true;
  }

  function dimDelta(h) {
    return typeof h.dimDeltaMm === "number" ? h.dimDeltaMm : Infinity;
  }

  function applyFiltersAndSort(list) {
    const out = list.filter(matchesFilters);
    if (sortKey === "closest") {
      out.sort((a, b) => dimDelta(a) - dimDelta(b));
    } else if (sortKey === "brand") {
      out.sort((a, b) => {
        const ab = (a.brand || "").toLowerCase();
        const bb = (b.brand || "").toLowerCase();
        if (ab !== bb) return ab < bb ? -1 : 1;
        const ap = (a.partNumber || "").toLowerCase();
        const bp = (b.partNumber || "").toLowerCase();
        return ap < bp ? -1 : ap > bp ? 1 : 0;
      });
    }
    // "relevance" keeps server order.
    return out;
  }

  function renderToolbar() {
    if (!toolbar || !lastSearch) return;
    const mode = lastSearch.mode;
    const hits = lastSearch.hits;

    // Facet chips.
    if (filtersEl) {
      filtersEl.innerHTML = "";
      FACETS.forEach((f) => {
        const counts = new Map();
        hits.forEach((h) => {
          const v = f.get(h);
          counts.set(v, (counts.get(v) || 0) + 1);
        });
        if (counts.size < 2) return; // single-bucket facet is noise
        const group = document.createElement("div");
        group.className = "interchange-search__filter-group";
        group.innerHTML =
          '<span class="interchange-search__filter-label">' + esc(f.label) + "</span>"
          + '<div class="interchange-search__filter-chips"></div>';
        const chipsWrap = group.querySelector(".interchange-search__filter-chips");
        const buckets = Array.from(counts.entries())
          .sort((a, b) => (b[1] - a[1]) || (a[0] < b[0] ? -1 : 1));
        buckets.forEach((entry) => {
          const value = entry[0];
          const count = entry[1];
          const pressed = !!(activeFacets[f.key] && activeFacets[f.key].has(value));
          const chip = document.createElement("button");
          chip.type = "button";
          chip.className = "interchange-search__chip";
          chip.setAttribute("aria-pressed", pressed ? "true" : "false");
          chip.dataset.facet = f.key;
          chip.dataset.value = value;
          chip.innerHTML = esc(value)
            + ' <span class="interchange-search__chip-count">' + count + "</span>";
          chip.addEventListener("click", () => toggleFacet(f.key, value));
          chipsWrap.appendChild(chip);
        });
        filtersEl.appendChild(group);
      });

      // Clear filters.
      if (filtersActive()) {
        const clear = document.createElement("button");
        clear.type = "button";
        clear.className = "interchange-search__act";
        clear.textContent = "Clear filters";
        clear.addEventListener("click", () => {
          activeFacets = {};
          IFX.track("ifx_filter_change", { facet: "clear" });
          renderSearchResults();
        });
        filtersEl.appendChild(clear);
      }
    }

    // Sort select.
    if (sortEl) {
      const opts = [["relevance", "Sort: Relevance"]];
      if (mode === "dims") opts.push(["closest", "Sort: Closest size"]);
      opts.push(["brand", "Sort: Brand A–Z"]);
      if (!opts.some((o) => o[0] === sortKey)) sortKey = "relevance";
      sortEl.innerHTML = opts.map((o) =>
        '<option value="' + o[0] + '"' + (o[0] === sortKey ? " selected" : "") + ">" + esc(o[1]) + "</option>"
      ).join("");
      sortEl.hidden = false;
    }

    toolbar.hidden = false;
  }

  function toggleFacet(key, value) {
    if (!activeFacets[key]) activeFacets[key] = new Set();
    if (activeFacets[key].has(value)) activeFacets[key].delete(value);
    else activeFacets[key].add(value);
    if (activeFacets[key].size === 0) delete activeFacets[key];
    IFX.track("ifx_filter_change", { facet: key });
    renderSearchResults();
    refocusChip(key, value);
  }

  // Re-rendering the toolbar destroys the clicked chip; restore keyboard focus.
  function refocusChip(facet, value) {
    if (!filtersEl) return;
    const chips = filtersEl.querySelectorAll(".interchange-search__chip");
    for (let i = 0; i < chips.length; i++) {
      if (chips[i].dataset.facet === facet && chips[i].dataset.value === value) {
        chips[i].focus();
        return;
      }
    }
  }

  if (sortEl) {
    sortEl.addEventListener("change", () => {
      sortKey = sortEl.value;
      IFX.track("ifx_filter_change", { facet: "sort" });
      renderSearchResults();
    });
  }

  /* ── §10 (removed) ───────────────────────────────────────────────────────
     Add-to-cart, product links, and price display were removed by policy:
     for now the only card action is "Request a quote". The server still
     returns apolloMatch data; restore commerce CTAs from git history when
     the business decides to re-enable them. */

  /* ── §11 (removed) ───────────────────────────────────────────────────────
     Bulk CSV export and print were removed by data policy: interchange
     reference data must not be bulk-extractable. The @media print stylesheet
     suppresses result data as the other half of the same policy. */

  /* ── §12 PN autocomplete ─────────────────────────────────────────────── */

  let acHighlightIdx = -1;
  let acItems = [];

  if (pnInput && acDropdown) {
    // Anchor the dropdown to the input wrap / row (nearest positioned box).
    const acRow = pnInput.closest(".interchange-search__input-wrap")
      || pnInput.closest(".interchange-search__input-row");
    if (acRow && acDropdown.parentElement !== acRow) acRow.appendChild(acDropdown);
    if (!acDropdown.id) acDropdown.id = "ifx-ac-list";
    acDropdown.setAttribute("role", "listbox");
    acDropdown.setAttribute("aria-label", "Part number suggestions");
    pnInput.setAttribute("role", "combobox");
    pnInput.setAttribute("aria-autocomplete", "list");
    pnInput.setAttribute("aria-expanded", "false");
    pnInput.setAttribute("aria-controls", acDropdown.id);
  }

  const debouncedAutocomplete = IFX.debounce(function (q) { fetchAutocomplete(q); }, AC_DEBOUNCE_MS);

  if (pnInput) {
    pnInput.addEventListener("input", () => {
      const v = pnInput.value.trim();
      if (v.length < 2) {
        acSeq++; // invalidate any in-flight fetch
        hideAutocomplete();
        return;
      }
      debouncedAutocomplete(v);
    });
    pnInput.addEventListener("keydown", (e) => {
      if (acItems.length === 0) {
        if (e.key === "Escape") hideAutocomplete();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        acHighlightIdx = (acHighlightIdx + 1) % acItems.length;
        updateAcHighlight();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        acHighlightIdx = (acHighlightIdx - 1 + acItems.length) % acItems.length;
        updateAcHighlight();
      } else if (e.key === "Enter" && acHighlightIdx >= 0) {
        e.preventDefault();
        pickAutocomplete(acHighlightIdx);
      } else if (e.key === "Escape") {
        hideAutocomplete();
      }
    });
  }

  function showAcRows(html) {
    if (!acDropdown) return;
    acDropdown.innerHTML = html;
    acDropdown.hidden = false;
    if (pnInput) pnInput.setAttribute("aria-expanded", "true");
    acHighlightIdx = -1;
    if (pnInput) pnInput.removeAttribute("aria-activedescendant");
  }

  async function fetchAutocomplete(q) {
    const seq = ++acSeq;
    acItems = [];
    showAcRows('<div class="interchange-search__autocomplete-loading">Searching part numbers…</div>');
    try {
      const url = new URL(API_BASE + "/api/autocomplete");
      url.searchParams.set("q", q);
      url.searchParams.set("limit", "8");
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (seq !== acSeq) return; // stale
      if (!r.ok) return hideAutocomplete();
      const data = await r.json();
      if (seq !== acSeq) return;
      if (!pnInput || pnInput.value.trim().length < 2) return hideAutocomplete();
      acItems = data.suggestions || [];
      if (acItems.length === 0) {
        showAcRows('<div class="interchange-search__autocomplete-empty">No suggestions — press Enter to search</div>');
        return;
      }
      renderAutocomplete();
    } catch (err) {
      if (seq === acSeq) hideAutocomplete();
    }
  }

  function renderAutocomplete() {
    showAcRows(acItems.map((s, i) =>
      '<div class="interchange-search__autocomplete-item" role="option" id="ifx-ac-opt-' + i
      + '" aria-selected="false" data-i="' + i + '">'
      + "<span>" + esc(s.partNumber) + "</span>"
      + '<span class="interchange-search__autocomplete-brand">' + esc((s.brand || "").toUpperCase()) + "</span>"
      + "</div>"
    ).join(""));
    acDropdown.querySelectorAll(".interchange-search__autocomplete-item").forEach((el) => {
      el.addEventListener("click", () => pickAutocomplete(Number(el.dataset.i)));
    });
  }

  function pickAutocomplete(i) {
    const item = acItems[i];
    if (!item || !pnInput) return;
    pnInput.value = item.partNumber;
    IFX.track("ifx_autocomplete_pick", { pn: item.partNumber, brand: item.brand || "" });
    hideAutocomplete();
    submitForm(pnInput.form);
  }

  function updateAcHighlight() {
    if (!acDropdown) return;
    acDropdown.querySelectorAll(".interchange-search__autocomplete-item").forEach((el, i) => {
      const on = i === acHighlightIdx;
      el.classList.toggle("is-highlighted", on);
      el.setAttribute("aria-selected", on ? "true" : "false");
    });
    if (pnInput) {
      if (acHighlightIdx >= 0) pnInput.setAttribute("aria-activedescendant", "ifx-ac-opt-" + acHighlightIdx);
      else pnInput.removeAttribute("aria-activedescendant");
    }
  }

  function hideAutocomplete() {
    if (!acDropdown) return;
    acDropdown.hidden = true;
    acDropdown.innerHTML = "";
    acItems = [];
    acHighlightIdx = -1;
    if (pnInput) {
      pnInput.setAttribute("aria-expanded", "false");
      pnInput.removeAttribute("aria-activedescendant");
    }
  }

  /* ── §13 Decode + glossary (/api/decode) ─────────────────────────────── */

  root.querySelectorAll('[data-action="open-glossary"]').forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      loadGlossary();
    });
  });

  async function runDecode(pn) {
    const seq = ++searchSeq;
    writeUrlState({ tab: "decode", q: pn });
    setStatus(spinnerHtml() + "Decoding " + esc(pn) + "…");
    renderSkeletons("block");
    try {
      const url = new URL(API_BASE + "/api/decode");
      url.searchParams.set("q", pn);
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API " + r.status);
      const data = await r.json();
      if (seq !== searchSeq) return;
      renderDecode(data);
      IFX.track("ifx_decode", { query: pn });
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error("[decode] error", err);
      renderError(
        "Decoder unavailable",
        "We couldn't decode that part number right now. Check your connection and try again.",
        () => runDecode(pn)
      );
    }
  }

  function renderDecode(data) {
    setStatus("Decoded " + esc(data.raw || "") + ".");
    results.innerHTML = "";
    hideToolbar();
    const card = document.createElement("div");
    card.className = "interchange-search__decode-card";

    const reference = data.reference || [];

    const componentsHtml = (data.components || []).length === 0
      ? (reference.length
          ? ""
          : '<p class="interchange-search__decode-empty">No suffix codes detected — this looks like the bare bearing designation. The base alone identifies the geometry; no special modifiers were specified.</p>')
      : (data.components || []).map((c) => {
          const codeChip = c.rawCode
            ? '<span class="interchange-search__decode-rawcode">' + esc(c.rawCode) + "</span>"
            : "";
          const brandTag = c.rawBrands && c.rawBrands.length
            ? '<span class="interchange-search__decode-brands">' + esc(c.rawBrands.map((b) => b.toUpperCase()).join(", ")) + "</span>"
            : "";
          const detailMeaning = c.rawMeaning && c.rawMeaning !== c.label
            ? '<span class="interchange-search__decode-notes">' + esc(c.rawMeaning) + "</span>"
            : "";
          return '<li class="interchange-search__decode-item">'
            + '<span class="interchange-search__decode-category">' + esc(catLabel(c.category, data.categories)) + "</span>"
            + '<span class="interchange-search__decode-meaning">' + codeChip + esc(c.label || "") + "</span>"
            + brandTag + detailMeaning
            + "</li>";
        }).join("");

    // Brand nomenclature reference: codes the cross-brand glossary doesn't
    // carry, explained per manufacturer. Several brands may read the same
    // letters differently, so each meaning is attributed.
    const referenceHtml = reference.length
      ? '<div class="interchange-search__decode-reference">'
        + '<div class="interchange-search__decode-reflabel">Manufacturer nomenclature</div>'
        + '<p class="interchange-search__decode-refnote">These codes are brand-specific — the same letters can mean different things depending on who made the bearing.</p>'
        + reference.map((hit) => {
            const extra = hit.totalBrands > hit.meanings.length
              ? '<li class="interchange-search__decode-refmore">+ ' + esc(hit.totalBrands - hit.meanings.length) + " more manufacturer(s)</li>"
              : "";
            return '<div class="interchange-search__decode-refgroup">'
              + '<span class="interchange-search__decode-rawcode">' + esc(hit.code) + "</span>"
              + '<ul class="interchange-search__decode-reflist">'
              + hit.meanings.map((m) =>
                  '<li class="interchange-search__decode-refitem">'
                  + '<span class="interchange-search__decode-brands">' + esc(String(m.brand).toUpperCase()) + "</span>"
                  + '<span class="interchange-search__decode-meaning">' + esc(m.meaning) + "</span>"
                  + (m.appliesTo ? '<span class="interchange-search__decode-notes">Applies to: ' + esc(m.appliesTo) + "</span>" : "")
                  + "</li>"
                ).join("")
              + extra
              + "</ul></div>";
          }).join("")
        + "</div>"
      : "";

    // Only apologise for what is still genuinely unexplained — a token the
    // reference layer just resolved is no longer "unparsed" to the customer.
    const explained = new Set(reference.map((h) => String(h.code).toUpperCase()));
    const stillUnparsed = String(data.unparsed || "")
      .split("/")
      .map((t) => t.trim())
      .filter((t) => t && !explained.has(t.toUpperCase()));
    const unparsedHtml = stillUnparsed.length
      ? '<div class="interchange-search__decode-unparsed">Unparsed: <code>' + esc(stillUnparsed.join("/")) + "</code> (brand-specific code we don't decode yet)</div>"
      : "";

    const bi = data.baseInfo || {};
    const dimsLine = bi.boreMm
      ? esc(bi.boreMm) + (bi.odMm ? " × " + esc(bi.odMm) : "") + (bi.widthMm ? " × " + esc(bi.widthMm) : "") + "&nbsp;mm"
        + (bi.dimsSource === "db" ? " (from catalog)" : bi.dimsSource === "iso_formula" ? " (bore from ISO formula)" : "")
      : "";
    const typeLine = bi.bearingTypeName
      ? '<div class="interchange-search__decode-basetype"><strong>' + esc(bi.bearingTypeName) + "</strong>"
        + (bi.series ? " — " + esc(bi.series) + " series" : "") + "</div>"
      : "";
    const seriesNoteLine = bi.seriesNote
      ? '<div class="interchange-search__decode-basenote">' + esc(bi.seriesNote) + "</div>"
      : "";
    const dimsHtml = dimsLine
      ? '<div class="interchange-search__decode-basedims"><strong>Dimensions:</strong> ' + dimsLine + "</div>"
      : "";
    const boreNote = bi.boreNote && !bi.odMm
      ? '<div class="interchange-search__decode-basenote">' + esc(bi.boreNote) + "</div>"
      : "";

    card.innerHTML =
      '<div class="interchange-search__decode-header">'
      + '<span class="interchange-search__decode-label">Decoded</span>'
      + '<span class="interchange-search__decode-raw">' + esc(data.raw || "") + "</span>"
      + "</div>"
      + '<div class="interchange-search__decode-base">'
      + "<div><strong>Base:</strong> <code>" + esc(data.base || "") + "</code></div>"
      + typeLine + dimsHtml + seriesNoteLine + boreNote
      + "</div>"
      + '<ul class="interchange-search__decode-list">' + componentsHtml + "</ul>"
      + referenceHtml
      + unparsedHtml
      + '<div class="interchange-search__decode-footer">'
      + '<button type="button" class="interchange-search__decode-toggle-btn" data-action="open-glossary">Browse full glossary &rarr;</button>'
      + "</div>";
    results.appendChild(card);

    card.querySelectorAll('[data-action="open-glossary"]').forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        loadGlossary();
      });
    });
  }

  function catLabel(slug, categories) {
    if (!categories) return slug;
    const c = categories.find((x) => x.slug === slug);
    return c ? c.name : slug;
  }

  async function loadGlossary() {
    const seq = ++searchSeq;
    setStatus(spinnerHtml() + "Loading glossary…");
    renderSkeletons("block");
    try {
      const r = await fetch(API_BASE + "/api/decode", { credentials: "omit" });
      if (!r.ok) throw new Error("API " + r.status);
      const data = await r.json();
      if (seq !== searchSeq) return;
      renderGlossary(data);
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error("[decode] glossary error", err);
      renderError(
        "Glossary unavailable",
        "We couldn't load the suffix glossary. Check your connection and try again.",
        () => loadGlossary()
      );
    }
  }

  async function loadBrandReference(slug) {
    const seq = ++searchSeq;
    setStatus(spinnerHtml() + "Loading " + esc(slug.toUpperCase()) + " nomenclature…");
    renderSkeletons("block");
    try {
      const url = new URL(API_BASE + "/api/decode");
      url.searchParams.set("brand", slug);
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API " + r.status);
      const data = await r.json();
      if (seq !== searchSeq) return;
      renderBrandReference(data);
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error("[decode] brand reference error", err);
      renderError(
        "Nomenclature unavailable",
        "We couldn't load that manufacturer's nomenclature right now.",
        () => loadBrandReference(slug)
      );
    }
  }

  function renderBrandReference(data) {
    const codes = data.codes || [];
    setStatus(codes.length + " " + esc(String(data.brand).toUpperCase()) + " codes.");
    results.innerHTML = "";
    hideToolbar();
    const block = document.createElement("div");
    block.className = "interchange-search__decode-card";
    block.innerHTML =
      '<div class="interchange-search__decode-header">'
      + '<span class="interchange-search__decode-label">' + esc(String(data.brand).toUpperCase()) + "</span>"
      + "</div>"
      + '<p class="interchange-search__decode-catdesc">Prefixes and suffixes as this manufacturer uses them. Codes are brand-specific and do not carry across makers.</p>'
      + '<ul class="interchange-search__decode-list">'
      + codes.map((c) =>
          '<li class="interchange-search__decode-item">'
          + '<span class="interchange-search__decode-code">' + esc(c.code) + "</span>"
          + '<span class="interchange-search__decode-meaning">' + esc(c.meaning) + "</span>"
          + '<span class="interchange-search__decode-brands">' + esc(c.role === "unspecified" ? "CODE" : c.role.toUpperCase()) + "</span>"
          + (c.appliesTo ? '<span class="interchange-search__decode-notes">Applies to: ' + esc(c.appliesTo) + "</span>" : "")
          + "</li>"
        ).join("")
      + "</ul>"
      + '<div class="interchange-search__decode-footer">'
      + '<button type="button" class="interchange-search__decode-toggle-btn" data-action="open-glossary">&larr; Back to the full glossary</button>'
      + "</div>";
    results.appendChild(block);
    block.querySelectorAll('[data-action="open-glossary"]').forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        loadGlossary();
      });
    });
  }

  function renderGlossary(data) {
    setStatus((Number(data.totalCodes) || 0) + " codes across " + (data.categories || []).length + " categories.");
    results.innerHTML = "";
    hideToolbar();

    // Per-manufacturer nomenclature, for codes the cross-brand glossary below
    // doesn't carry — including legacy lines with no other public reference.
    const brands = data.referenceBrands || [];
    if (brands.length) {
      const picker = document.createElement("div");
      picker.className = "interchange-search__decode-card";
      picker.innerHTML =
        '<div class="interchange-search__decode-header">'
        + '<span class="interchange-search__decode-label">By manufacturer</span>'
        + "</div>"
        + '<p class="interchange-search__decode-catdesc">Prefix and suffix meanings for a specific maker.</p>'
        + '<div class="interchange-search__decode-brandpicker">'
        + brands.map((b) =>
            '<button type="button" class="interchange-search__decode-brandbtn" data-brand="' + esc(b.slug) + '">'
            + esc(String(b.slug).replace(/_/g, " ").toUpperCase())
            + '<span class="interchange-search__decode-brandcount">' + esc(b.codes) + "</span>"
            + "</button>"
          ).join("")
        + "</div>";
      picker.querySelectorAll("[data-brand]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          loadBrandReference(btn.getAttribute("data-brand"));
        });
      });
      results.appendChild(picker);
    }

    (data.categories || []).forEach((cat) => {
      const codes = (data.byCategory || {})[cat.slug] || [];
      if (codes.length === 0) return;
      const block = document.createElement("div");
      block.className = "interchange-search__decode-card";
      block.innerHTML =
        '<div class="interchange-search__decode-header">'
        + '<span class="interchange-search__decode-label">' + esc(cat.name) + "</span>"
        + "</div>"
        + '<p class="interchange-search__decode-catdesc">' + esc(cat.description) + "</p>"
        + '<ul class="interchange-search__decode-list">'
        + codes.map((c) =>
          '<li class="interchange-search__decode-item">'
          + '<span class="interchange-search__decode-code">' + esc(c.code) + "</span>"
          + '<span class="interchange-search__decode-meaning">' + esc(c.meaning) + "</span>"
          + (c.brands && c.brands.length
            ? '<span class="interchange-search__decode-brands">' + esc(c.brands.map((b) => b.toUpperCase()).join(", ")) + "</span>"
            : '<span class="interchange-search__decode-brands">UNIVERSAL</span>')
          + (c.equivalents && c.equivalents.length
            ? '<span class="interchange-search__decode-equiv">Equivalents: ' + c.equivalents.map(esc).join(" · ") + "</span>"
            : "")
          + (c.notes ? '<span class="interchange-search__decode-notes">' + esc(c.notes) + "</span>" : "")
          + "</li>"
        ).join("")
        + "</ul>";
      results.appendChild(block);
    });
  }

  /* ── §14 Equipment (/api/equipment) ──────────────────────────────────── */

  async function loadManufacturers() {
    if (!mfgSelect) return;
    mfgSelect.disabled = true;
    mfgSelect.innerHTML = '<option value="">Loading…</option>';
    try {
      const r = await fetch(API_BASE + "/api/equipment", { credentials: "omit" });
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      const list = data.manufacturers || [];
      if (list.length === 0) {
        mfgSelect.innerHTML = '<option value="">No equipment data available yet</option>';
        setStatus("We haven't published equipment data yet — check back soon, or search by part number instead.");
        return;
      }
      mfgSelect.innerHTML = '<option value="">Select manufacturer…</option>'
        + list.map((m) =>
          '<option value="' + escAttr(m.manufacturer) + '">' + esc(m.manufacturer)
          + " (" + (Number(m.model_count) || 0) + ")</option>"
        ).join("");
      mfgSelect.disabled = false;
    } catch (err) {
      console.error("[interchange] manufacturers load error", err);
      oemLoaded = false; // let re-opening the tab retry
      mfgSelect.innerHTML = '<option value="">Failed to load — try again later</option>';
      if (currentTab === "oem") {
        renderError(
          "Couldn't load the equipment list",
          "Check your connection and try again.",
          () => { oemLoaded = true; loadManufacturers(); }
        );
      }
    }
  }

  async function loadModels(manufacturer) {
    if (!modelSelect) return;
    modelSelect.disabled = true;
    modelSelect.innerHTML = '<option value="">Loading…</option>';
    try {
      const url = new URL(API_BASE + "/api/equipment");
      url.searchParams.set("manufacturer", manufacturer);
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      const list = data.models || [];
      if (list.length === 0) {
        modelSelect.innerHTML = '<option value="">No models listed for this manufacturer yet</option>';
        setStatus("No models listed for " + esc(manufacturer) + " yet — try another manufacturer or search by part number.");
        return;
      }
      modelSelect.innerHTML = '<option value="">Select model…</option>'
        + list.map((m) =>
          '<option value="' + escAttr(m.model) + '">' + esc(m.model)
          + (m.machine_type ? " — " + esc(m.machine_type.replace(/_/g, " ")) : "") + "</option>"
        ).join("");
      modelSelect.disabled = false;
    } catch (err) {
      console.error("[interchange] models load error", err);
      modelSelect.innerHTML = '<option value="">Failed to load — reselect manufacturer to retry</option>';
      if (currentTab === "oem") {
        renderError(
          "Couldn't load models for " + manufacturer,
          "Check your connection and try again.",
          () => loadModels(manufacturer)
        );
      }
    }
  }

  if (mfgSelect) {
    mfgSelect.addEventListener("change", () => {
      clearResults();
      const m = mfgSelect.value;
      if (!m) {
        if (modelSelect) {
          modelSelect.innerHTML = '<option value="">Choose manufacturer first</option>';
          modelSelect.disabled = true;
        }
        return;
      }
      loadModels(m);
    });
  }

  if (modelSelect) {
    modelSelect.addEventListener("change", () => {
      const mfg = mfgSelect ? mfgSelect.value : "";
      const model = modelSelect.value;
      if (!mfg || !model) return clearResults();
      loadMachineBearings(mfg, model);
    });
  }

  async function loadMachineBearings(manufacturer, model) {
    const seq = ++searchSeq;
    setStatus(spinnerHtml() + "Loading bearings for " + esc(manufacturer) + " " + esc(model) + "…");
    renderSkeletons("cards");
    try {
      const url = new URL(API_BASE + "/api/equipment");
      url.searchParams.set("manufacturer", manufacturer);
      url.searchParams.set("model", model);
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      if (seq !== searchSeq) return;
      renderOemResults(data);
      if (data.machine && data.machine.id) {
        writeUrlState({ tab: "oem", machineId: data.machine.id });
      }
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error("[interchange] equipment search error", err);
      renderError(
        "Couldn't load bearings for that machine",
        "Check your connection and try again.",
        () => loadMachineBearings(manufacturer, model)
      );
    }
  }

  // NEW: load a machine directly by id (?machineId= deep links + free-text picks).
  async function loadMachineById(machineId) {
    const seq = ++searchSeq;
    writeUrlState({ tab: "oem", machineId: machineId });
    setStatus(spinnerHtml() + "Loading machine…");
    renderSkeletons("cards");
    try {
      const url = new URL(API_BASE + "/api/equipment");
      url.searchParams.set("machineId", machineId);
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      if (seq !== searchSeq) return;
      renderOemResults(data);
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error("[interchange] machine load error", err);
      renderError(
        "Couldn't load that machine",
        "Check your connection and try again.",
        () => loadMachineById(machineId)
      );
    }
  }

  function renderOemResults(data) {
    const bearings = data.bearings || [];
    const machineName = data.machine
      ? ((data.machine.manufacturer || "") + " " + (data.machine.model || "")).trim()
      : "that machine";
    hideToolbar();
    if (bearings.length === 0) {
      setStatus("");
      results.innerHTML = "";
      const empty = document.createElement("div");
      empty.className = "interchange-search__empty";
      empty.innerHTML =
        '<div class="interchange-search__empty-title">No bearings recorded for ' + esc(machineName) + " yet</div>"
        + "<p>Request a quote and our team will look up the bearings for your machine.</p>";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "interchange-search__btn-rfq";
      btn.textContent = "Request a quote";
      btn.addEventListener("click", () => {
        IFX.track("ifx_cta_click", { kind: "rfq" });
        openRfq("", "");
      });
      empty.appendChild(btn);
      results.appendChild(empty);
      return;
    }
    setStatus(bearings.length + " bearing" + (bearings.length === 1 ? "" : "s") + " in " + esc(machineName) + ".");
    results.innerHTML = "";
    bearings.forEach((b) => results.appendChild(buildOemCard(b)));
  }

  function buildOemCard(b) {
    const card = document.createElement("div");
    card.className = "interchange-search__card";
    const brand = (b.bearing_brand || "").toUpperCase();
    // DB values are low | medium | high | critical. Only flag the two levels a
    // customer should act on; "low"/"medium" badges are noise.
    const crit = (b.criticality || "").toLowerCase();
    const critBadge = (crit === "critical" || crit === "high")
      ? '<span class="interchange-search__oem-crit interchange-search__oem-crit--' + escAttr(crit) + '">' + esc(crit) + "</span>"
      : "";
    const oemPn = b.oem_part_number && b.oem_part_confidence >= 0.6 ? b.oem_part_number : null;
    const qty = Number(b.quantity);
    card.innerHTML =
      '<div class="interchange-search__card-brand">' + esc(brand) + " " + critBadge + "</div>"
      + '<div class="interchange-search__card-pn">' + esc(b.bearing_pn || "") + "</div>"
      + (oemPn ? '<div class="interchange-search__oem-pn">OEM PN: <strong>' + esc(oemPn) + "</strong></div>" : "")
      + '<div class="interchange-search__oem-location">' + esc(b.location || "") + (qty > 1 ? " &times; " + qty : "") + "</div>"
      + (b.bearing_type ? '<div class="interchange-search__card-type">' + esc(b.bearing_type.replace(/_/g, " ")) + "</div>" : "")
      + '<div class="interchange-search__card-cta"></div>';
    const cta = card.querySelector(".interchange-search__card-cta");
    const xref = document.createElement("button");
    xref.type = "button";
    xref.className = "interchange-search__btn-link";
    xref.textContent = "See cross-references";
    xref.addEventListener("click", () => {
      IFX.track("ifx_cta_click", { kind: "xref_jump" });
      activateTabByName("pn");
      if (pnInput) {
        pnInput.value = b.bearing_pn || "";
        submitForm(pnInput.form);
      }
    });
    cta.appendChild(xref);
    addRfqButton(cta, b.bearing_pn, b.bearing_brand);
    return card;
  }

  // ── Free-text machine search (NEW ?q=) ────────────────────────────────
  let oemSuggestItems = [];
  let oemSuggestIdx = -1;

  if (oemSearchInput && oemSuggest) {
    if (!oemSuggest.id) oemSuggest.id = "ifx-oem-suggest";
    oemSuggest.setAttribute("aria-label", "Machine suggestions");
    oemSearchInput.setAttribute("role", "combobox");
    oemSearchInput.setAttribute("aria-autocomplete", "list");
    oemSearchInput.setAttribute("aria-expanded", "false");
    oemSearchInput.setAttribute("aria-controls", oemSuggest.id);
  }

  const debouncedOemSearch = IFX.debounce(function (q) { fetchOemSuggest(q); }, OEM_DEBOUNCE_MS);

  if (oemSearchInput) {
    oemSearchInput.addEventListener("input", () => {
      const v = oemSearchInput.value.trim();
      if (v.length < 2) {
        oemAcSeq++;
        hideOemSuggest();
        return;
      }
      debouncedOemSearch(v);
    });
    oemSearchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        // Enter picks the highlighted (or single) machine; never submits.
        e.preventDefault();
        if (oemSuggestItems.length > 0) {
          pickOemSuggest(oemSuggestIdx >= 0 ? oemSuggestIdx : 0);
        }
        return;
      }
      if (oemSuggestItems.length === 0) {
        if (e.key === "Escape") hideOemSuggest();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        oemSuggestIdx = (oemSuggestIdx + 1) % oemSuggestItems.length;
        updateOemHighlight();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        oemSuggestIdx = (oemSuggestIdx - 1 + oemSuggestItems.length) % oemSuggestItems.length;
        updateOemHighlight();
      } else if (e.key === "Escape") {
        hideOemSuggest();
      }
    });
  }

  function showOemRows(html) {
    if (!oemSuggest) return;
    oemSuggest.innerHTML = html;
    oemSuggest.hidden = false;
    if (oemSearchInput) oemSearchInput.setAttribute("aria-expanded", "true");
    oemSuggestIdx = -1;
    if (oemSearchInput) oemSearchInput.removeAttribute("aria-activedescendant");
  }

  async function fetchOemSuggest(q) {
    const seq = ++oemAcSeq;
    oemSuggestItems = [];
    showOemRows('<div class="interchange-search__oem-suggest-loading">Searching machines…</div>');
    try {
      const url = new URL(API_BASE + "/api/equipment");
      url.searchParams.set("q", q);
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (seq !== oemAcSeq) return;
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      if (seq !== oemAcSeq) return;
      if (!oemSearchInput || oemSearchInput.value.trim().length < 2) return hideOemSuggest();
      oemSuggestItems = Array.isArray(data.machines) ? data.machines : [];
      IFX.track("ifx_oem_search", { query: q, result_count: oemSuggestItems.length });
      if (oemSuggestItems.length === 0) {
        showOemRows('<div class="interchange-search__oem-suggest-empty">No machines matched — try the brand or model number, or browse below.</div>');
        return;
      }
      renderOemSuggest();
    } catch (err) {
      if (seq !== oemAcSeq) return;
      console.error("[interchange] machine search error", err);
      showOemRows('<div class="interchange-search__oem-suggest-empty">Couldn’t search machines — check your connection and keep typing to retry.</div>');
    }
  }

  function renderOemSuggest() {
    showOemRows(oemSuggestItems.map((m, i) => {
      const type = m.machineType ? String(m.machineType).replace(/_/g, " ") : "";
      const n = Number(m.applicationCount) || 0;
      return '<div class="interchange-search__oem-suggest-item" role="option" id="ifx-oem-opt-' + i
        + '" aria-selected="false" data-i="' + i + '">'
        + "<span>" + esc((m.manufacturer || "") + " " + (m.model || "")) + (type ? " — " + esc(type) : "") + "</span>"
        + '<span class="interchange-search__autocomplete-brand">' + n + " bearing" + (n === 1 ? "" : "s") + "</span>"
        + "</div>";
    }).join(""));
    oemSuggest.querySelectorAll(".interchange-search__oem-suggest-item").forEach((el) => {
      el.addEventListener("click", () => pickOemSuggest(Number(el.dataset.i)));
    });
  }

  function updateOemHighlight() {
    if (!oemSuggest) return;
    oemSuggest.querySelectorAll(".interchange-search__oem-suggest-item").forEach((el, i) => {
      const on = i === oemSuggestIdx;
      el.classList.toggle("is-highlighted", on);
      el.setAttribute("aria-selected", on ? "true" : "false");
    });
    if (oemSearchInput) {
      if (oemSuggestIdx >= 0) oemSearchInput.setAttribute("aria-activedescendant", "ifx-oem-opt-" + oemSuggestIdx);
      else oemSearchInput.removeAttribute("aria-activedescendant");
    }
  }

  function pickOemSuggest(i) {
    const m = oemSuggestItems[i];
    if (!m) return;
    hideOemSuggest();
    if (oemSearchInput) {
      oemSearchInput.value = ((m.manufacturer || "") + " " + (m.model || "")).trim();
    }
    if (m.id) loadMachineById(m.id);
  }

  function hideOemSuggest() {
    if (!oemSuggest) return;
    oemSuggest.hidden = true;
    oemSuggest.innerHTML = "";
    oemSuggestItems = [];
    oemSuggestIdx = -1;
    if (oemSearchInput) {
      oemSearchInput.setAttribute("aria-expanded", "false");
      oemSearchInput.removeAttribute("aria-activedescendant");
    }
  }

  /* ── §15 Catalogs (/api/catalogs) ────────────────────────────────────── */

  let catalogsData = null; // cached payload — lazy, once per session
  let catalogsLoading = false;
  let catalogBrandFilter = ""; // "" = all · "other" = multi-brand · else slug

  const CATALOG_KIND_LABELS = {
    catalog: "Product catalog",
    interchange: "Interchange guide",
    reference: "Reference guide",
  };

  function catalogsTabActive() {
    return currentTab === "catalogs";
  }

  async function openCatalogs() {
    if (catalogsData) return renderCatalogs();
    if (catalogsLoading) return;
    catalogsLoading = true;
    setStatus(spinnerHtml() + "Loading catalogs…");
    renderSkeletons("cards");
    try {
      const r = await fetch(API_BASE + "/api/catalogs", { credentials: "omit" });
      if (!r.ok) throw new Error("API responded " + r.status);
      const data = await r.json();
      catalogsData = {
        brands: Array.isArray(data.brands) ? data.brands : [],
        catalogs: Array.isArray(data.catalogs) ? data.catalogs : [],
      };
      catalogBrandFilter = "";
      renderCatalogs();
    } catch (err) {
      console.error("[interchange] catalogs load error", err);
      // Only touch the shared status/results areas if the user is still on
      // this tab (the fetch may resolve after they switched away).
      if (catalogsTabActive()) {
        renderError(
          "Couldn't load catalogs",
          "Check your connection and try again.",
          () => openCatalogs()
        );
      }
    } finally {
      catalogsLoading = false;
    }
  }

  function renderCatalogs() {
    if (!catalogsData || !catalogsTabActive()) return;
    const all = catalogsData.catalogs;
    hideToolbar();

    if (all.length === 0) {
      if (catalogFilters) catalogFilters.hidden = true;
      setStatus("");
      results.innerHTML = "";
      const empty = document.createElement("div");
      empty.className = "interchange-search__empty";
      empty.innerHTML =
        '<div class="interchange-search__empty-title">No catalogs published yet</div>'
        + "<p>We're adding manufacturer catalogs and interchange guides — check back soon.</p>";
      results.appendChild(empty);
      return;
    }

    renderCatalogFilters();
    const filtered = catalogBrandFilter === ""
      ? all
      : catalogBrandFilter === "other"
        ? all.filter((c) => c.brand == null)
        : all.filter((c) => c.brand === catalogBrandFilter);

    results.innerHTML = "";
    if (filtered.length === 0) {
      setStatus("No documents for that brand yet — pick another brand.");
      return;
    }
    setStatus(filtered.length + " document" + (filtered.length === 1 ? "" : "s")
      + (catalogBrandFilter === ""
        ? " across " + catalogsData.brands.length + " brand" + (catalogsData.brands.length === 1 ? "" : "s")
        : "")
      + ".");
    filtered.forEach((c) => results.appendChild(buildCatalogCard(c)));
  }

  function renderCatalogFilters() {
    if (!catalogFilters || !catalogsData) return;
    const chips = [{ key: "", name: "All brands", count: catalogsData.catalogs.length }].concat(
      catalogsData.brands.map((b) => ({
        key: b.slug == null ? "other" : String(b.slug),
        name: b.name,
        count: b.count,
      }))
    );
    catalogFilters.innerHTML = "";
    chips.forEach((chip) => {
      const btn = document.createElement("button");
      btn.type = "button";
      const active = chip.key === catalogBrandFilter;
      btn.className = "interchange-search__catalog-chip" + (active ? " is-active" : "");
      btn.setAttribute("aria-pressed", active ? "true" : "false");
      btn.innerHTML = esc(chip.name)
        + ' <span class="interchange-search__catalog-chip-count">' + (Number(chip.count) || 0) + "</span>";
      btn.addEventListener("click", () => {
        catalogBrandFilter = chip.key;
        renderCatalogs();
      });
      catalogFilters.appendChild(btn);
    });
    catalogFilters.hidden = false;
  }

  function formatCatalogBytes(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n < 1024) return n + " B";
    if (n < 1024 * 1024) return Math.round(n / 1024) + " KB";
    return (Math.round((n / (1024 * 1024)) * 10) / 10) + " MB";
  }

  function buildCatalogCard(c) {
    const card = document.createElement("div");
    card.className = "interchange-search__card interchange-search__catalog-card";
    const kindLabel = Object.prototype.hasOwnProperty.call(CATALOG_KIND_LABELS, c.kind)
      ? CATALOG_KIND_LABELS[c.kind]
      : "Document";
    const metaBits = [kindLabel];
    const pages = Number(c.pages);
    if (Number.isFinite(pages) && pages > 0) metaBits.push(pages + (pages === 1 ? " page" : " pages"));
    const size = formatCatalogBytes(c.bytes);
    if (size) metaBits.push(size);
    const year = Number(c.year);
    if (Number.isFinite(year) && year > 0) metaBits.push(String(year));
    card.innerHTML =
      '<div class="interchange-search__card-brand">' + esc((c.brandName || "").toUpperCase()) + "</div>"
      + '<div class="interchange-search__catalog-title">' + esc(c.title || c.fileName || "Untitled document") + "</div>"
      + (c.description ? '<div class="interchange-search__catalog-desc">' + esc(c.description) + "</div>" : "")
      + '<div class="interchange-search__catalog-meta">' + metaBits.map((m) => esc(m)).join(" &middot; ") + "</div>"
      + '<div class="interchange-search__card-cta"></div>';
    const url = typeof c.url === "string" ? c.url : "";
    if (/^https?:\/\//i.test(url) || url.startsWith("/")) {
      const link = document.createElement("a");
      link.className = "interchange-search__btn-link";
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = "View PDF";
      link.addEventListener("click", () => IFX.track("ifx_cta_click", { kind: "catalog_pdf" }));
      card.querySelector(".interchange-search__card-cta").appendChild(link);
    }
    return card;
  }

  /* ── §16 RFQ dialog ──────────────────────────────────────────────────
     Native <dialog>.showModal() with a manual focus-trap fallback for
     browsers without dialog support. Reusable indefinitely — the form is
     never innerHTML-replaced. */

  let rfqOpener = null;      // element to restore focus to on close
  let rfqFallbackOpen = false;
  const supportsDialog = !!(modal && typeof modal.showModal === "function");

  function setModalError(message) {
    if (!modalError) return;
    if (message) {
      modalError.textContent = message;
      modalError.hidden = false;
    } else {
      modalError.textContent = "";
      modalError.hidden = true;
    }
  }

  function rfqFocusables() {
    if (!modal) return [];
    const sel = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
    return Array.from(modal.querySelectorAll(sel)).filter((el) =>
      el.tabIndex >= 0 && el.offsetWidth + el.offsetHeight > 0
    );
  }

  function rfqTrapKeydown(e) {
    if (!rfqFallbackOpen) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeRfq();
      return;
    }
    if (e.key !== "Tab") return;
    const items = rfqFocusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function restoreRfqFocus() {
    if (rfqOpener && typeof rfqOpener.focus === "function" && document.contains(rfqOpener)) {
      rfqOpener.focus();
    }
    rfqOpener = null;
  }

  function openRfq(partNumber, brand) {
    if (!modal || !rfqForm) return;
    rfqOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const pnField = rfqForm.querySelector('input[name="requestedPn"]');
    const brandField = rfqForm.querySelector('input[name="requestedBrand"]');
    const startedField = rfqForm.querySelector('input[name="startedAt"]');
    if (pnField) pnField.value = partNumber || "";
    if (brandField) brandField.value = brand || "";
    if (startedField) startedField.value = String(Date.now()); // anti-bot timing signal
    // Honeypot field (name="website") stays untouched — bots fill it, we don't.

    const summary = rfqForm.querySelector(".interchange-search__rfq-summary");
    if (summary) {
      summary.textContent = partNumber
        ? "Quote requested: " + (brand || "").toUpperCase() + " " + partNumber
        : "Quote for an item we don't stock.";
    }
    setModalError(null);

    if (supportsDialog) {
      modal.showModal();
    } else {
      modal.setAttribute("open", "");
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");
      rfqFallbackOpen = true;
      document.addEventListener("keydown", rfqTrapKeydown, true);
    }

    IFX.track("ifx_rfq_open", { pn: partNumber || "" });

    const nameField = rfqForm.querySelector('input[name="name"]');
    if (nameField) {
      setTimeout(() => nameField.focus(), 30);
    }
  }

  function closeRfq() {
    if (!modal) return;
    if (supportsDialog) {
      if (modal.open) modal.close(); // 'close' event restores focus
    } else {
      modal.removeAttribute("open");
      rfqFallbackOpen = false;
      document.removeEventListener("keydown", rfqTrapKeydown, true);
      restoreRfqFocus();
    }
  }

  if (modal && rfqForm) {
    modal.querySelectorAll("[data-close]").forEach((el) => el.addEventListener("click", closeRfq));

    // Backdrop click: a click that lands on the <dialog> itself (not the
    // panel) can only be the backdrop.
    modal.addEventListener("click", (e) => {
      if (e.target === modal) closeRfq();
    });

    if (supportsDialog) {
      // Native Esc → 'cancel' → 'close'. Restore focus on every close path.
      modal.addEventListener("close", restoreRfqFocus);
    }

    rfqForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      setModalError(null);
      const fd = new FormData(rfqForm);
      const body = {
        name: String(fd.get("name") || "").trim(),
        email: String(fd.get("email") || "").trim(),
        requestedPn: String(fd.get("requestedPn") || ""),
        qty: Math.max(1, Number(fd.get("qty")) || 1),
        // Honeypot MUST be present (and empty for humans) — sent verbatim.
        website: String(fd.get("website") == null ? "" : fd.get("website")),
        startedAt: Number(fd.get("startedAt")) || Date.now(),
      };
      const brand = String(fd.get("requestedBrand") || "").trim();
      if (brand) body.requestedBrand = brand;
      const phone = String(fd.get("phone") || "").trim();
      if (phone) body.phone = phone;
      const notes = String(fd.get("notes") || "").trim();
      if (notes) body.notes = notes;

      const submitBtn = rfqForm.querySelector('button[type="submit"]');
      const originalLabel = submitBtn ? submitBtn.textContent : "";
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "Sending…";
      }
      try {
        const r = await fetch(API_BASE + "/api/rfq", {
          method: "POST",
          credentials: "omit",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        let data = null;
        try { data = await r.json(); } catch (parseErr) { data = null; }
        if (!r.ok || !data || data.ok === false) {
          const msg = data && data.message
            ? String(data.message)
            : "Couldn't send your request — please try again, or email sales@apollousa.com directly.";
          throw Object.assign(new Error("RFQ failed"), { userMessage: msg });
        }
        closeRfq();
        IFX.showToast("Quote request sent — reference " + (data.reference || data.id || "pending"), { tone: "ok" });
        rfqForm.reset();
        IFX.track("ifx_rfq_submit", { ok: true });
      } catch (err) {
        console.error("[interchange] rfq submit error", err);
        setModalError(err && err.userMessage
          ? err.userMessage
          : "Couldn't send your request — please try again, or email sales@apollousa.com directly.");
        IFX.track("ifx_rfq_submit", { ok: false });
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel || "Send request";
        }
      }
    });
  }

  /* ── §17 Misc a11y + input niceties ──────────────────────────────────── */

  function setUnit(unit) {
    currentUnit = unit === "in" ? "in" : "mm";
    unitButtons.forEach((b) => {
      const active = b.dataset.unit === currentUnit;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-checked", active ? "true" : "false");
    });
    root.querySelectorAll(".interchange-search__dim input").forEach((inp) => {
      const ph = inp.dataset[currentUnit === "mm" ? "unitPlaceholderMm" : "unitPlaceholderIn"];
      if (ph) inp.placeholder = ph;
    });
  }

  unitButtons.forEach((btn) => {
    btn.addEventListener("click", () => setUnit(btn.dataset.unit));
    // Radiogroup arrow-key support.
    btn.addEventListener("keydown", (e) => {
      const list = Array.from(unitButtons);
      let idx = list.indexOf(btn);
      if (e.key === "ArrowRight" || e.key === "ArrowDown") idx = (idx + 1) % list.length;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") idx = (idx - 1 + list.length) % list.length;
      else return;
      e.preventDefault();
      list[idx].focus();
      setUnit(list[idx].dataset.unit);
    });
  });

  // Esc dismisses the disclaimer tooltip (shown on hover/focus via CSS).
  if (disclaimer) {
    disclaimer.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        disclaimer.blur();
      }
    });
  }

  // Click outside the tool closes both suggestion dropdowns.
  document.addEventListener("click", (e) => {
    if (!root.contains(e.target)) {
      hideAutocomplete();
      hideOemSuggest();
    }
  });

  /* ── §18 Init: URL state restore, popstate ───────────────────────────── */

  /**
   * Restore state from location.search. A bare ?q= with no tab (the mini
   * widget's deep link) is treated as tab=pn.
   */
  function applyUrlState(sp) {
    const tab = sp.get("tab") || (sp.get("q") ? "pn" : null);
    if (!tab) {
      activateTabByName("pn", { silent: true });
      return;
    }
    restoringUrl = true;
    try {
      if (tab === "pn") {
        activateTabByName("pn", { silent: true });
        const q = (sp.get("q") || "").trim();
        if (q) {
          if (pnInput) pnInput.value = q;
          executePnSearch(q);
        }
      } else if (tab === "dims") {
        activateTabByName("dims", { silent: true });
        if (sp.get("unit") === "in") setUnit("in");
        const tolParam = sp.get("tol");
        if (tolSelect && tolParam) {
          const has = Array.from(tolSelect.options).some((o) => o.value === tolParam);
          if (has) tolSelect.value = tolParam;
        }
        const bore = sp.get("bore") != null && sp.get("bore") !== "" ? Number(sp.get("bore")) : null;
        const od = sp.get("od") != null && sp.get("od") !== "" ? Number(sp.get("od")) : null;
        const width = sp.get("width") != null && sp.get("width") !== "" ? Number(sp.get("width")) : null;
        // Prefill inputs for display (URL values are always mm).
        if (dimsForm) {
          const fill = (name, mm) => {
            const inp = dimsForm.querySelector('input[name="' + name + '"]');
            if (!inp) return;
            if (mm == null || !Number.isFinite(mm)) { inp.value = ""; return; }
            inp.value = currentUnit === "in" ? String(mmToInches(mm)) : String(mm);
          };
          fill("bore", bore);
          fill("od", od);
          fill("width", width);
        }
        const params = {};
        if (bore != null && Number.isFinite(bore)) params.bore = bore;
        if (od != null && Number.isFinite(od)) params.od = od;
        if (width != null && Number.isFinite(width)) params.width = width;
        params.tol = tolSelect ? Number(tolSelect.value) || DEFAULT_TOL : DEFAULT_TOL;
        if (params.bore != null || params.od != null || params.width != null) {
          executeDimsSearch(params);
        }
      } else if (tab === "decode") {
        activateTabByName("decode", { silent: true });
        const q = (sp.get("q") || "").trim();
        if (q) {
          const decodeInput = root.querySelector('input[name="decode_q"]');
          if (decodeInput) decodeInput.value = q;
          runDecode(q);
        }
      } else if (tab === "oem") {
        activateTabByName("oem", { silent: true });
        const machineId = (sp.get("machineId") || "").trim();
        if (machineId) loadMachineById(machineId);
      } else if (tab === "catalogs") {
        activateTabByName("catalogs", { silent: true });
      } else {
        activateTabByName("pn", { silent: true });
      }
    } finally {
      restoringUrl = false;
    }
  }

  window.addEventListener("popstate", () => {
    const sp = new URLSearchParams(location.search);
    // Back to a clean URL → the next search should push a fresh entry again.
    didPushState = OWNED_PARAMS.some((p) => sp.has(p));
    applyUrlState(sp);
  });

  // Initial deep-link restore. If the landing URL already carries our params
  // the current history entry represents that state, so the auto-run must
  // not push a duplicate.
  (function init() {
    const sp = new URLSearchParams(location.search);
    if (OWNED_PARAMS.some((p) => sp.has(p))) {
      didPushState = true;
      applyUrlState(sp);
    }
  })();
})();
